import * as React from 'react';
import {Box, Card, CardContent, Skeleton, Tooltip, Typography} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import {useTranslation} from 'react-i18next';
import type {DashboardStats} from '../../hooks/useDashboard';
import {formatEUR} from '../../hooks/useDashboard';

interface DashboardStatCardsProps {
    readonly stats: DashboardStats;
}

interface StatCardProps {
    readonly icon: React.ReactElement;
    readonly label: string;
    readonly value: string;
    readonly caption?: string;
    readonly loading?: boolean;
    readonly unavailableHint?: string;
}

function StatCard({icon, label, value, caption, loading, unavailableHint}: Readonly<StatCardProps>): React.ReactElement {
    const content = (
        <Card variant="outlined" sx={{borderRadius: 1, height: '100%'}}>
            <CardContent sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                alignItems: 'flex-start',
                gap: 1.5,
                p: {xs: 1.5, md: 2},
                '&:last-child': {pb: {xs: 1.5, md: 2}},
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: 'secondary.light',
                    color: 'secondary.dark',
                    flexShrink: 0,
                }}>
                    {icon}
                </Box>
                <Box sx={{textAlign: 'left', minWidth: 0, width: '100%'}}>
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                        {label}
                    </Typography>
                    {loading ? (
                        <Skeleton variant="text" width={90} height={32}/>
                    ) : (
                        <Typography variant="h6" sx={{
                            fontWeight: 700,
                            fontSize: {xs: '1rem', md: '1.25rem'},
                            overflowWrap: 'anywhere',
                        }}>
                            {value}
                        </Typography>
                    )}
                    {caption && (
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                            {caption}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    return unavailableHint ? <Tooltip title={unavailableHint} arrow>{content}</Tooltip> : content;
}

export default function DashboardStatCards({stats}: Readonly<DashboardStatCardsProps>): React.ReactElement {
    const {t} = useTranslation();
    const financialsUnavailableHint = !stats.financialsAvailable && !stats.loading
        ? t('dashboard.stats.financialsUnavailable')
        : undefined;

    const preliminaryCaption = stats.financialsAvailable && !stats.financialsFinal && !stats.loading
        ? t('dashboard.stats.preliminary')
        : undefined;

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: {xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'},
            gap: 2,
        }}>
            <StatCard
                icon={<PeopleAltOutlinedIcon/>}
                label={t('dashboard.stats.employeesInPayroll')}
                value={String(stats.employeeCount)}
                caption={t('dashboard.stats.branchCount', {count: stats.branchCount})}
            />
            <StatCard
                icon={<SavingsOutlinedIcon/>}
                label={t('dashboard.stats.grossOfMonth')}
                value={formatEUR(stats.gross)}
                loading={stats.loading}
                caption={preliminaryCaption}
                unavailableHint={financialsUnavailableHint}
            />
            <StatCard
                icon={<TrendingUpOutlinedIcon/>}
                label={t('dashboard.stats.projectedNet')}
                value={formatEUR(stats.netPay)}
                loading={stats.loading}
                caption={preliminaryCaption}
                unavailableHint={financialsUnavailableHint}
            />
            <StatCard
                icon={<CalculateOutlinedIcon/>}
                label={t('dashboard.stats.employerCost')}
                value={formatEUR(stats.employerCost)}
                loading={stats.loading}
                caption={preliminaryCaption}
                unavailableHint={financialsUnavailableHint}
            />
        </Box>
    );
}
